import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  dateFnsLocalizer,
  Views,
  type Components,
  type SlotInfo,
  type View,
} from "react-big-calendar";
import {
  addDays,
  format,
  getDay,
  isWithinInterval,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";
import { useIsMobileViewport } from "@/hooks";
import type { AvailabilityCalendarEvent } from "@/types";
import AvailabilityCalendarToolbar from "./AvailabilityCalendarToolbar";
import type { MappedCalendarEvent } from "./availabilityCalendar.types";
import ThreeDayView from "./ThreeDayView";
import { THREE_DAY_VIEW, type ThreeDayViewName } from "./threeDayView.constants";
import "./AvailabilityCalendar.scss";

type AvailabilityCalendarView = View | ThreeDayViewName;

const CALENDAR_COMPONENTS: Components<MappedCalendarEvent> = {
  toolbar: AvailabilityCalendarToolbar,
};

const SELECTION_EVENT_ID = "__availability-selection__";

const LONG_PRESS_THRESHOLD_MS = 500;
const HOUR_START = 5;
const SLOT_MINUTES = 30;

const CALENDAR_MIN = new Date(1970, 0, 1, HOUR_START, 0, 0);
const CALENDAR_MAX = new Date(1970, 0, 1, 23, 59, 59);

const startOfWeekMonday = (date: Date) =>
  startOfWeek(date, { weekStartsOn: 1 });

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: startOfWeekMonday,
  getDay,
  locales: { es },
});

const CALENDAR_MESSAGES = {
  showMore: (total: number) => `+${total} más`,
  today: "Hoy",
  previous: "Anterior",
  next: "Siguiente",
  month: "Mes",
  week: "Semana",
  day: "Día",
  threeDay: "3 días",
} as const;

export interface CalendarSelectionRange {
  start: Date;
  end: Date;
}

export interface SelectionActivatePayload {
  range: CalendarSelectionRange;
  hasConflict: boolean;
}

export type CalendarFocusedTarget = null | "selection" | string;

export interface AvailabilityCalendarProps {
  events: AvailabilityCalendarEvent[];
  placeholderEventTitle?: string;
  longPressThreshold?: number;
  weekStart?: Date;
  onWeekChange?: (weekStart: Date) => void;
  view?: AvailabilityCalendarView;
  onViewChange?: (view: AvailabilityCalendarView) => void;
  selectionRange?: CalendarSelectionRange | null;
  onSelectionRangeChange?: (range: CalendarSelectionRange | null) => void;
  onRangeSelect?: (range: CalendarSelectionRange) => void;
  focusedTarget?: CalendarFocusedTarget;
  onFocusedTargetChange?: (target: CalendarFocusedTarget) => void;
  manageInteractions?: boolean;
  onSelectionActivate?: (payload: SelectionActivatePayload) => void;
  onEventActivate?: (event: AvailabilityCalendarEvent) => void;
}

const isTimeGridView = (view: AvailabilityCalendarView): boolean =>
  view === Views.WEEK || view === THREE_DAY_VIEW;

const resolveTimeGridView = (isMobile: boolean): AvailabilityCalendarView =>
  isMobile ? THREE_DAY_VIEW : Views.WEEK;

const resolveCalendarView = (
  view: AvailabilityCalendarView,
  isMobile: boolean,
): AvailabilityCalendarView => {
  if (view === Views.MONTH) {
    return Views.MONTH;
  }

  return resolveTimeGridView(isMobile);
};

const resolveCalendarDate = (
  date: Date,
  resolvedView: AvailabilityCalendarView,
): Date => {
  if (resolvedView === THREE_DAY_VIEW) {
    return startOfDay(date);
  }

  if (resolvedView === Views.WEEK) {
    return startOfWeekMonday(date);
  }

  const weekStart = startOfWeekMonday(date);
  const weekEnd = addDays(weekStart, 6);
  const today = startOfDay(new Date());

  if (isWithinInterval(today, { start: weekStart, end: weekEnd })) {
    return startOfMonth(today);
  }

  return startOfMonth(date);
};

const alignDateToView = (
  date: Date,
  view: AvailabilityCalendarView,
  isMobile: boolean,
): Date => resolveCalendarDate(date, resolveCalendarView(view, isMobile));

const areIntervalsOverlapping = (
  startA: Date,
  endA: Date,
  startB: Date,
  endB: Date,
): boolean => startA < endB && endA > startB;

const getEventFocusKey = (event: AvailabilityCalendarEvent): string =>
  event.sourceEventId ?? event.id;

const mapEvents = (
  events: AvailabilityCalendarEvent[],
): MappedCalendarEvent[] =>
  events.map((event) => ({
    ...event,
    start: new Date(event.startsAt),
    end: new Date(event.endsAt),
    title: event.title?.trim() || "Ocupado",
  }));

/**
 * Week grid calendar with drag-to-select datetime range and occupied slots
 */
const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  events,
  weekStart: weekStartProp,
  onWeekChange,
  view: viewProp,
  onViewChange,
  selectionRange: selectionRangeProp,
  onSelectionRangeChange,
  onRangeSelect,
  focusedTarget: focusedTargetProp,
  onFocusedTargetChange,
  manageInteractions = false,
  placeholderEventTitle = "Horario seleccionado",
  longPressThreshold = LONG_PRESS_THRESHOLD_MS,
  onSelectionActivate,
  onEventActivate,
}) => {
  const isMobile = useIsMobileViewport();
  const [internalWeekStart, setInternalWeekStart] = useState(() =>
    startOfMonth(new Date()),
  );
  const [internalView, setInternalView] =
    useState<AvailabilityCalendarView>(Views.MONTH);
  const [internalSelectionRange, setInternalSelectionRange] =
    useState<CalendarSelectionRange | null>(null);
  const [internalFocusedTarget, setInternalFocusedTarget] =
    useState<CalendarFocusedTarget>(null);

  const isSelectionControlled = onSelectionRangeChange !== undefined;
  const isFocusControlled = onFocusedTargetChange !== undefined;
  const selectionActivateEnabled = Boolean(onSelectionActivate);
  const readonlyOccupiedEvents =
    selectionActivateEnabled && !manageInteractions;
  const selectionRange = isSelectionControlled
    ? (selectionRangeProp ?? null)
    : internalSelectionRange;
  const focusedTarget = isFocusControlled
    ? (focusedTargetProp ?? null)
    : internalFocusedTarget;

  const updateFocusedTarget = useCallback(
    (target: CalendarFocusedTarget) => {
      if (isFocusControlled) {
        onFocusedTargetChange(target);
      } else {
        setInternalFocusedTarget(target);
      }
    },
    [isFocusControlled, onFocusedTargetChange],
  );

  const weekStart = weekStartProp ?? internalWeekStart;
  const calendarView = viewProp ?? internalView;
  const calendarEvents = useMemo(() => mapEvents(events), [events]);

  const resolvedView = useMemo(
    () => resolveCalendarView(calendarView, isMobile),
    [calendarView, isMobile],
  );

  const resolvedDate = useMemo(
    () => resolveCalendarDate(weekStart, resolvedView),
    [weekStart, resolvedView],
  );

  const calendarViews = useMemo(
    () =>
      isMobile
        ? { month: true, [THREE_DAY_VIEW]: ThreeDayView }
        : { month: true, week: true },
    [isMobile],
  );

  const updateSelectionRange = useCallback(
    (range: CalendarSelectionRange) => {
      if (isSelectionControlled) {
        onSelectionRangeChange(range);
      } else {
        setInternalSelectionRange(range);
      }

      onRangeSelect?.(range);
    },
    [isSelectionControlled, onRangeSelect, onSelectionRangeChange],
  );

  const clearSelectionRange = useCallback(() => {
    if (isSelectionControlled) {
      onSelectionRangeChange?.(null);
    } else {
      setInternalSelectionRange(null);
    }
  }, [isSelectionControlled, onSelectionRangeChange]);

  const displayEvents = useMemo(() => {
    if (!selectionRange || !isTimeGridView(resolvedView)) {
      return calendarEvents;
    }

    return [
      ...calendarEvents,
      {
        id: SELECTION_EVENT_ID,
        startsAt: selectionRange.start.toISOString(),
        endsAt: selectionRange.end.toISOString(),
        start: selectionRange.start,
        end: selectionRange.end,
        title: placeholderEventTitle,
      },
    ];
  }, [calendarEvents, placeholderEventTitle, resolvedView, selectionRange]);

  const selectionConflict = useMemo(() => {
    if (!selectionRange) {
      return false;
    }

    return events.some((event) =>
      areIntervalsOverlapping(
        selectionRange.start,
        selectionRange.end,
        new Date(event.startsAt),
        new Date(event.endsAt),
      ),
    );
  }, [events, selectionRange]);

  const selectedEvent = useMemo(() => {
    if (!focusedTarget || focusedTarget === "selection") {
      return undefined;
    }

    return displayEvents.find(
      (event) =>
        event.id !== SELECTION_EVENT_ID &&
        getEventFocusKey(event) === focusedTarget,
    );
  }, [displayEvents, focusedTarget]);

  const setCalendarDate = useCallback(
    (date: Date, view: AvailabilityCalendarView = calendarView) => {
      const alignedDate = alignDateToView(date, view, isMobile);

      if (onWeekChange) {
        onWeekChange(alignedDate);
      } else {
        setInternalWeekStart(alignedDate);
      }
    },
    [calendarView, isMobile, onWeekChange],
  );

  const handleViewChange = useCallback(
    (view: AvailabilityCalendarView) => {
      if (onViewChange) {
        onViewChange(view);
      } else {
        setInternalView(view);
      }
    },
    [onViewChange],
  );

  const goToTimeGridContaining = useCallback(
    (date: Date) => {
      const targetView = isMobile ? THREE_DAY_VIEW : Views.WEEK;

      handleViewChange(targetView);
      setCalendarDate(date, targetView);
    },
    [handleViewChange, isMobile, setCalendarDate],
  );

  const handleNavigate = useCallback(
    (newDate: Date) => {
      setCalendarDate(newDate, resolvedView);
    },
    [resolvedView, setCalendarDate],
  );

  useEffect(() => {
    if (
      resolvedView === calendarView &&
      resolvedDate.getTime() === weekStart.getTime()
    ) {
      return;
    }

    if (resolvedView !== calendarView) {
      handleViewChange(resolvedView);
    }

    if (resolvedDate.getTime() !== weekStart.getTime()) {
      if (onWeekChange) {
        onWeekChange(resolvedDate);
      } else {
        setInternalWeekStart(resolvedDate);
      }
    }
  }, [
    calendarView,
    handleViewChange,
    onWeekChange,
    resolvedDate,
    resolvedView,
    weekStart,
  ]);

  const handleEventPropGetter = useCallback(
    (event: MappedCalendarEvent) => {
      if (event.id === SELECTION_EVENT_ID) {
        return {
          className: [
            "availability-calendar__selection-range",
            selectionConflict &&
              "availability-calendar__selection-range--conflict",
          ]
            .filter(Boolean)
            .join(" "),
        };
      }

      if (event.displayScope === "internal") {
        return { className: "availability-calendar__event--internal" };
      }

      if (event.displayScope === "pending") {
        return { className: "availability-calendar__event--pending" };
      }

      if (event.displayScope === "room") {
        return { className: "availability-calendar__event--room" };
      }

      return { className: "availability-calendar__event--occupied" };
    },
    [selectionConflict],
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      if (
        manageInteractions &&
        isTimeGridView(resolvedView) &&
        selectionRange &&
        slotInfo.action === "click"
      ) {
        clearSelectionRange();
        updateFocusedTarget(null);
        return;
      }

      updateSelectionRange({ start: slotInfo.start, end: slotInfo.end });

      if (manageInteractions && isTimeGridView(resolvedView)) {
        updateFocusedTarget(null);
      }
    },
    [
      clearSelectionRange,
      manageInteractions,
      resolvedView,
      selectionRange,
      updateFocusedTarget,
      updateSelectionRange,
    ],
  );

  const handleSelectEvent = useCallback(
    (event: MappedCalendarEvent) => {
      if (event.id === SELECTION_EVENT_ID) {
        if (
          !selectionActivateEnabled ||
          !isTimeGridView(resolvedView) ||
          !selectionRange
        ) {
          return;
        }

        if (focusedTarget === "selection") {
          onSelectionActivate?.({
            range: selectionRange,
            hasConflict: selectionConflict,
          });
          return;
        }

        updateFocusedTarget("selection");
        return;
      }

      if (resolvedView === Views.MONTH) {
        goToTimeGridContaining(event.start);
        return;
      }

      if (!manageInteractions || !isTimeGridView(resolvedView)) {
        return;
      }

      const eventKey = getEventFocusKey(event);

      if (focusedTarget === eventKey) {
        onEventActivate?.(event);
        return;
      }

      updateFocusedTarget(eventKey);
    },
    [
      focusedTarget,
      goToTimeGridContaining,
      manageInteractions,
      onEventActivate,
      onSelectionActivate,
      resolvedView,
      selectionActivateEnabled,
      selectionConflict,
      selectionRange,
      updateFocusedTarget,
    ],
  );

  const handleDrillDown = useCallback(
    (date: Date) => {
      goToTimeGridContaining(date);
    },
    [goToTimeGridContaining],
  );

  const handleGetDrilldownView = useCallback(
    (_date: Date, view: View, configuredViewNames: View[]) => {
      if (view !== Views.MONTH) {
        return null;
      }

      if (
        isMobile &&
        configuredViewNames.includes(THREE_DAY_VIEW as View)
      ) {
        return THREE_DAY_VIEW as View;
      }

      if (configuredViewNames.includes(Views.WEEK)) {
        return Views.WEEK;
      }

      return null;
    },
    [isMobile],
  );

  return (
    <div
      className={[
        "availability-calendar",
        readonlyOccupiedEvents && "availability-calendar--readonly-events",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Calendar<MappedCalendarEvent>
        localizer={localizer}
        culture="es"
        messages={CALENDAR_MESSAGES}
        views={calendarViews}
        view={resolvedView as View}
        onView={handleViewChange as (view: View) => void}
        date={resolvedDate}
        onNavigate={handleNavigate}
        events={displayEvents}
        selected={selectedEvent}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        step={SLOT_MINUTES}
        timeslots={2}
        min={CALENDAR_MIN}
        max={CALENDAR_MAX}
        selectable={isTimeGridView(resolvedView)}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onDrillDown={handleDrillDown}
        getDrilldownView={handleGetDrilldownView}
        popup
        components={CALENDAR_COMPONENTS}
        longPressThreshold={longPressThreshold}
        eventPropGetter={handleEventPropGetter}
      />
    </div>
  );
};

export default AvailabilityCalendar;
